import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { useProfile } from '@/hooks/useProfile';
import { useCreateComment } from '@/hooks/useComments';
import { useCreateMentionNotifications } from '@/hooks/useNotifications';
import { useToast } from '@/hooks/use-toast';
import { MentionInput } from './MentionInput';
import { Send, Loader2 } from 'lucide-react';
import { useMembershipContext } from '@/components/layout/AppLayout';

interface CommentInputProps {
  postId: string;
  parentCommentId?: string | null;
  placeholder?: string;
  onCommentAdded?: () => void;
  autoFocus?: boolean;
  compact?: boolean;
}

export function CommentInput({
  postId,
  parentCommentId,
  placeholder = 'Write a comment...',
  onCommentAdded,
  autoFocus = false,
  compact = false,
}: CommentInputProps) {
  const [content, setContent] = useState('');
  const { data: profile } = useProfile();
  const createComment = useCreateComment();
  const createMentionNotifications = useCreateMentionNotifications();
  const { toast } = useToast();
  const { isPending } = useMembershipContext();

  const handleSubmit = async () => {
    if (isPending) {
      toast({
        title: 'Complete your profile',
        description: 'Commenting unlocks after approval.',
        variant: 'destructive',
      });
      return;
    }
    if (!content.trim()) return;

    try {
      const comment = await createComment.mutateAsync({
        postId,
        content: content.trim(),
        parentCommentId,
      });

      // Send mention notifications
      if (content.includes('@')) {
        createMentionNotifications.mutate({
          content: content.trim(),
          postId,
          commentId: comment.id,
        });
      }

      setContent('');
      onCommentAdded?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (compact) {
    return (
      <div className="flex gap-2 items-start">
        <Avatar className="h-6 w-6 shrink-0">
          <SignedAvatarImage src={profile?.avatar_url || undefined} />
          <AvatarFallback
            className="text-white text-[9px]"
            style={{
              background: 'linear-gradient(135deg, hsl(340 82% 52%) 0%, hsl(255 100% 65%) 100%)',
            }}
          >
            {profile?.full_name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0 relative">
          <div className="w-full bg-muted/50 rounded-xl border border-border focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20 transition-all">
            <MentionInput
              placeholder={placeholder}
              value={content}
              onChange={setContent}
              onKeyDown={handleKeyDown}
              autoFocus={autoFocus}
              className="w-full min-h-[36px] py-2 px-3 pr-9 bg-transparent border-0 focus-visible:ring-0 resize-none text-xs leading-tight placeholder:text-muted-foreground/60"
              disabled={isPending}
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={handleSubmit}
              disabled={!content.trim() || createComment.isPending || isPending}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-primary hover:bg-primary/10 disabled:opacity-30"
            >
              {createComment.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex gap-2 sm:gap-3 items-start">
      <Avatar className="h-8 w-8 shrink-0 mt-0.5 hidden sm:flex">
        <SignedAvatarImage src={profile?.avatar_url || undefined} />
        <AvatarFallback
          className="text-white text-xs"
          style={{
            background: 'linear-gradient(135deg, hsl(340 82% 52%) 0%, hsl(255 100% 65%) 100%)',
          }}
        >
          {profile?.full_name?.charAt(0) || 'U'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 w-full min-w-0 relative">
        <div className="w-full bg-muted/50 rounded-2xl border border-border focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20 transition-all">
          <MentionInput
            placeholder={placeholder}
            value={content}
            onChange={setContent}
            onKeyDown={handleKeyDown}
            autoFocus={autoFocus}
            className="w-full min-h-[44px] py-2.5 px-3 sm:px-4 pr-11 bg-transparent border-0 focus-visible:ring-0 resize-none text-sm leading-tight placeholder:text-muted-foreground/60"
            disabled={isPending}
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={handleSubmit}
            disabled={!content.trim() || createComment.isPending || isPending}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full text-primary hover:bg-primary/10 disabled:opacity-30"
          >
            {createComment.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
